/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from '../logging/Log';

interface CellStatusEntry {
    enableTime: number;
    callback: () => void;
}

const TAG = 'CellStatusScheduler';

class CellStatusSchedulerImpl {
    private timerId: ReturnType<typeof setTimeout> | null = null;

    private readonly entries: Map<string, CellStatusEntry> = new Map();

    public register(id: string, enableTime: number, callback: () => void): void {
        if (enableTime <= Date.now()) {
            this.entries.delete(id);
            if (this.entries.size === 0) {
                this.stop();
            }
            try {
                callback();
            } catch (e) {
                Log.w(TAG, `Callback failed for entry '${id}'`, e);
            }
            return;
        }
        this.entries.set(id, { enableTime, callback });
        this.scheduleNext();
    }

    public unregister(id: string): void {
        this.entries.delete(id);
        if (this.entries.size === 0) {
            this.stop();
        } else {
            this.scheduleNext();
        }
    }

    private scheduleNext(): void {
        if (this.entries.size === 0) return;

        let earliest = Infinity;
        this.entries.forEach((entry) => {
            if (entry.enableTime < earliest) earliest = entry.enableTime;
        });

        const delay = Math.max(0, earliest - Date.now());
        this.stop();
        this.timerId = setTimeout(() => this.tick(), delay);
    }

    private tick(): void {
        this.timerId = null;
        const now = Date.now();
        const triggered: string[] = [];
        this.entries.forEach((entry, id) => {
            if (now >= entry.enableTime) {
                triggered.push(id);
            }
        });
        triggered.forEach((id) => {
            const entry = this.entries.get(id);
            this.entries.delete(id);
            try {
                entry?.callback();
            } catch (e) {
                Log.w(TAG, `Callback failed for entry '${id}'`, e);
            }
        });
        if (this.entries.size > 0) {
            this.scheduleNext();
        }
    }

    private stop(): void {
        if (this.timerId !== null) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
    }
}

export const CellStatusScheduler = new CellStatusSchedulerImpl();
